import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
	Button,
	Col,
	Flex,
	Form,
	Image,
	Input,
	message,
	notification,
	Row,
	Upload,
	Card,
	Divider,
	Tabs,
	Space,
	Typography,
} from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import uploadApi from '../../api/file';
import partnerApi from '../../api/partner';
import SH_Model from '../../components/SH_Model';
import Constant from '../../lib/constant';

const { Text } = Typography;

const defaultData = {
	names: {
		vi: '',
		en: '',
	},
	createdAtDate: new Date(),
	descriptions: {
		vi: '',
		en: '',
	},
	categories: [],
	images: [],
};

function TV_partner_detail(props, ref) {
	const { getProducts, categories, projects } = props;

	const [formInstance, setFormInstance] = useState();
	const [open, setOpen] = useState(false);
	const [index, setIndex] = useState(defaultData);
	const [form] = Form.useForm();
	const refMode = useRef();

	const [initValue, setInitValue] = useState(defaultData);
	const [api, contextHolder] = notification.useNotification();

	const [fileList, setFileList] = useState([]);
	const [uploading, setUploading] = useState(false);

	// Partner dùng 1 ảnh object: product.image
	const [images, setImages] = useState({});
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});

	const propsErrors = {
		namesvi: null,
		namesen: null,
		descriptionsvi: null,
		descriptionsen: null,
	};

	const openNotificationWithIcon = (type, messageText, description) => {
		api[type]({
			message: messageText,
			description,
		});
	};

	useImperativeHandle(ref, () => ({
		create: () => {
			setImages({});
			setFileList([]);
			setErrors(propsErrors);
			setOpen(true);
			refMode.current = Constant.crud.create;
		},
		update: (product) => {
			const { contents, ...remain } = product;
			setInitValue({ ...remain, contents: contents ? contents : { vi: '', en: '' } });
			setImages(product.image || {});
			setIndex({ ...product, contents: contents ? contents : { vi: '', en: '' } });
			setFileList([]);
			setErrors(propsErrors);
			setOpen(true);
			refMode.current = Constant.crud.update;
		},
	}));

	useEffect(() => {
		onFormInstanceReady(form);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onFormInstanceReady = (instance) => setFormInstance(instance);

	const validate = (propsValidate, data) => {
		const _data = structuredClone(
			data || { ...initValue, contents: index.contents, descriptions: index.descriptions, images }
		);
		const _errors = structuredClone(errors);

		if (!propsValidate) propsValidate = propsErrors;

		Object.keys(propsValidate).forEach((prop) => {
			switch (prop) {
				case 'descriptionsvi':
					_errors[prop] = _data['descriptions']?.['vi']?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'descriptionsen':
					_errors[prop] = _data['descriptions']?.['en']?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'namesvi':
					_errors[prop] = _data?.['names']?.['vi']?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'namesen':
					_errors[prop] = _data?.['names']?.['en']?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				default:
					_errors[prop] = _data[prop]?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
			}
		});

		setErrors(_errors);
		return Object.values(_errors);
	};

	const handleSubmit = async () => {
		setInitValue({
			...initValue,
			createdAtDate: index.createdAtDate,
			contents: index.contents,
			descriptions: index.descriptions,
			images,
		});

		try {
			const _errorList = validate();

			if (_errorList.filter((x) => x).length === 0) {
				if (refMode.current === Constant.crud.update) {
					setLoading(true);
					const { _id, names, categories } = initValue;

					await partnerApi.update(_id, {
						names,
						createdAtDate: index.createdAtDate,
						image: { url: images.url, signature: images.signature, public_id: images.public_id },
						categories,
						contents: index.contents,
						descriptions: index.descriptions,
					});

					openNotificationWithIcon('success', 'Thành công', 'Thao tác thành công');
					onCancel();
					getProducts();
					setLoading(false);
				} else {
					setLoading(true);

					await partnerApi.create({
						...initValue,
						createdAtDate: index.createdAtDate,
						image: { url: images.url, signature: images.signature, public_id: images.public_id },
						contents: index.contents,
						descriptions: index.descriptions,
					});

					openNotificationWithIcon('success', 'Thành công', 'Thao tác thành công');
					onCancel();
					getProducts();
					setLoading(false);
				}
			}
		} catch (err) {
			console.log(err);
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveImage = async () => {
		setImages({});
	};

	const onCancel = () => {
		setOpen(false);
		refMode.current = '';
		formInstance?.resetFields();
		setInitValue(defaultData);
		setIndex(defaultData);
		setErrors(propsErrors);
		setImages({});
		setFileList([]);
	};

	function uploadPlugin(editor) {
		editor.plugins.get('FileRepository').createUploadAdapter = (loader) => uploadAdapter(loader);
	}

	function uploadAdapter(loader) {
		return {
			upload: () =>
				new Promise(async (resolve, reject) => {
					try {
						const file = await loader.file;
						if (file) {
							uploadApi
								.uploadImage(file)
								.then((data) => {
									resolve({ default: data.data.data.url });
									message.success('upload successfully.');
								})
								.catch(() => {
									message.error('upload failed.');
								});
						}
					} catch (error) {
						reject('Error');
					}
				}),
			abort: () => {},
		};
	}

	const handleUpload = async () => {
		setUploading(true);
		const response = await uploadApi.uploadImage(fileList[0]);
		setImages(response.data.data);
		message.success('upload successfully.');
		setUploading(false);
	};

	const prop = {
		onRemove: () => {
			setFileList([]);
		},
		beforeUpload: (file) => {
			setFileList([file]);
			return false;
		},
		fileList,
	};

	const isCreate = refMode.current === Constant.crud.create;

	return (
		<>
			{contextHolder}

			<SH_Model
				width="80%"
				open={open}
				handleSubmit={handleSubmit}
				onCancel={onCancel}
				textOk={isCreate ? 'Tạo mới' : 'Cập nhật'}
				loading={loading}
				title={isCreate ? 'Tạo mới đối tác' : 'Cập nhật đối tác'}
				heights="75vh"
			>
				<Form form={form} layout="vertical" requiredMark={false}>
					<Card size="small" title="Thông tin cơ bản" style={{ borderRadius: 12 }}>
						<Row gutter={16}>
							<Col span={12}>
								<Form.Item label="Tên đối tác (Tiếng Việt)" required>
									<Input
										placeholder="Nhập tên đối tác (VI)"
										value={initValue.names?.vi}
										onChange={(e) =>
											setInitValue((prev) => ({
												...prev,
												names: { vi: e.target.value, en: prev.names.en },
											}))
										}
									/>
									<Text type="danger">{errors['namesvi']}</Text>
								</Form.Item>
							</Col>

							<Col span={12}>
								<Form.Item label="Tên đối tác (Tiếng Anh)" required>
									<Input
										placeholder="Enter partner name (EN)"
										value={initValue.names?.en}
										onChange={(e) =>
											setInitValue((prev) => ({
												...prev,
												names: { en: e.target.value, vi: prev.names.vi },
											}))
										}
									/>
									<Text type="danger">{errors['namesen']}</Text>
								</Form.Item>
							</Col>
						</Row>
					</Card>

					<Divider style={{ margin: '14px 0' }} />

					<Card size="small" title="Mô tả" style={{ borderRadius: 12 }}>
						<Tabs
							defaultActiveKey="vi"
							destroyInactiveTabPane={false}
							items={[
								{
									key: 'vi',
									label: 'Tiếng Việt',
									children: (
										<Space direction="vertical" size={10} style={{ width: '100%' }}>
											<div>
												<div style={{ fontWeight: 600, marginBottom: 8 }}>Mô tả (VI)</div>
												<CKEditor
													editor={ClassicEditor}
													data={index?.descriptions?.vi}
													config={{
														extraPlugins: [uploadPlugin],
														mediaEmbed: { previewsInData: true },
													}}
													onChange={(event, editor) => {
														const data = editor.getData();
														setIndex((prev) => ({
															...prev,
															descriptions: { vi: data, en: prev.descriptions.en },
														}));
													}}
												/>
												<Text type="danger">{errors['descriptionsvi']}</Text>
											</div>
										</Space>
									),
								},
								{
									key: 'en',
									label: 'English',
									children: (
										<Space direction="vertical" size={10} style={{ width: '100%' }}>
											<div>
												<div style={{ fontWeight: 600, marginBottom: 8 }}>Description (EN)</div>
												<CKEditor
													editor={ClassicEditor}
													data={index?.descriptions?.en}
													config={{
														extraPlugins: [uploadPlugin],
														mediaEmbed: { previewsInData: true },
													}}
													onChange={(event, editor) => {
														const data = editor.getData();
														setIndex((prev) => ({
															...prev,
															descriptions: { en: data, vi: prev.descriptions.vi },
														}));
													}}
												/>
												<Text type="danger">{errors['descriptionsen']}</Text>
											</div>
										</Space>
									),
								},
							]}
						/>
					</Card>

					<Divider style={{ margin: '14px 0' }} />

					<Card size="small" title="Hình ảnh" style={{ borderRadius: 12 }}>
						<Space direction="vertical" size={10} style={{ width: '100%' }}>
							<Flex align="center" justify="space-between" wrap="wrap" gap={10}>
								<Space>
									<Upload {...prop} maxCount={1}>
										<Button icon={<UploadOutlined />}>Chọn ảnh</Button>
									</Upload>

									<Button
										type="primary"
										onClick={handleUpload}
										disabled={fileList.length === 0}
										loading={uploading}
									>
										{uploading ? 'Đang tải' : 'Tải lên'}
									</Button>
								</Space>

								<Text type="secondary">{images?.url ? 'Đã có ảnh' : 'Chưa có ảnh'}</Text>
							</Flex>

							{images?.url ? (
								<div
									style={{
										position: 'relative',
										display: 'inline-block',
										borderRadius: 12,
										overflow: 'hidden',
										border: '1px solid #f0f0f0',
									}}
								>
									<Image width={160} height={110} style={{ objectFit: 'cover' }} src={images.url} />
									<Button
										danger
										type="primary"
										size="small"
										style={{ position: 'absolute', top: 6, right: 6, borderRadius: 999 }}
										onClick={() => handleRemoveImage(images)}
										icon={<DeleteOutlined />}
									/>
								</div>
							) : null}
						</Space>
					</Card>
				</Form>
			</SH_Model>
		</>
	);
}

export default forwardRef(TV_partner_detail);
